import React, { useState, createContext, useContext, useCallback } from 'react';
import { GroupType } from '../api/api';
import { PermMode } from '../components/WithPermission';
import { useAuth } from './AuthContext';
import { addTrackEvent, trackFilterEvents } from '../utils/piwikUtils';
import { Nullable, PiwikName } from '../@types/common';
import { GroupsPackage } from '../@types/Groups';

interface ContextType {
  type: GroupType | undefined;
  onTypeChange: (value: GroupType) => void;
  groupId: string;
  groupName: string;
  onGroupSelect: ({ id, name }: { id: string; name: string }) => void;
  selectedBenefitIdx: number | null;
  setBenefitIdx: (value: number | null) => void;
  activeTab: string;
  setActiveTab: (value: string) => void;
  initialValues: Nullable<GroupsPackage>;
  setInitialValues: (value: Nullable<GroupsPackage>) => void;
}

const initialValue: ContextType = {
  type: undefined,
  onTypeChange: (_value: GroupType) => {},
  groupId: '',
  groupName: '',
  onGroupSelect: ({ id, name }: { id: string; name: string }) => {},
  selectedBenefitIdx: null,
  setBenefitIdx: (_value: number | null) => {},
  activeTab: '',
  setActiveTab: (_value: string) => {},
  initialValues: null,
  setInitialValues: () => {}
};

const GroupContext = createContext(initialValue);

export const GroupContextProvider = ({ children }) => {
  const { checkPerm } = useAuth();
  const [type, setType] = useState<GroupType>(() =>
    checkPerm(['GroupRead'], PermMode.ALL) ? GroupType.ACTIVE : GroupType.DRAFT
  );
  const [groupId, setGroupId] = useState<string>('');
  const [groupName, setGroupName] = useState<string>('');
  const [selectedBenefitIdx, setBenefitIdx] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('');
  const [initialValues, setInitialValues] = useState<Nullable<GroupsPackage>>(null);

  const onTypeChange = useCallback(
    (newFilter: GroupType) => {
      if (type !== newFilter) {
        trackFilterEvents(newFilter);
        setType(newFilter);
        setGroupId('');
        setBenefitIdx(null);
      }
    },
    [type]
  );

  const onGroupSelect = useCallback(({ id, name }: { id: string; name: string }) => {
    addTrackEvent(PiwikName.MANAGE_DRAFT);
    setGroupId(id);
    setGroupName(name);
    setBenefitIdx(null);
  }, []);

  return (
    <GroupContext.Provider
      value={{
        type,
        onTypeChange,
        groupId,
        groupName,
        onGroupSelect,
        selectedBenefitIdx,
        setBenefitIdx,
        activeTab,
        setActiveTab,
        initialValues,
        setInitialValues
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};

export const useGroupContext = () => {
  return useContext(GroupContext);
};
